import React, { useState, useEffect, useRef, useCallback } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader";  
import Alert from '../common/Alert.js';
import vehicleService from '../../services/vehicleService.js';
import vehicleOwnerService from '../../services/VehicleOwnerService.js';
import { enrichVehiclesWithOwnerDetails } from '../../utils/vehicleOwnerDisplay.js';
import VehicleOwnerTooltip from './VehicleOwnerTooltip.js';
import { Truck, Plus, FileDown, Sheet } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { formatLocationPointsSummary, formatLocationPointsDisplay } from '../../utils/vehicleLocationPoints.js';
import { VEHICLE_TYPE_OPTIONS, formatVehicleType } from '../../utils/vehicleTypes.js';
import { useTranslation } from 'react-i18next';
import BulkExcelModal from '../common/BulkExcelModal.js';
import { downloadBlob } from '../../utils/downloadBlob.js';
import { useAuth } from '../../context/AuthContext';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';

/** Indian-style number plate badge (yellow + blue strip) — vehicle number only. */
function VehicleNumberPlate({ number, onClick, className = '' }) {
  const label = number || '—';
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`inline-flex items-stretch max-w-full rounded-md border border-slate-800 overflow-hidden shadow-sm hover:shadow transition-shadow focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-1 ${className}`}
    >
      <span className="w-2 sm:w-2.5 bg-[#1e3a8a] shrink-0" aria-hidden="true" />
      <span className="px-2 sm:px-2.5 py-1 bg-[#facc15] text-slate-900 font-bold text-xs sm:text-sm tracking-wide uppercase font-mono leading-none truncate">
        {label}
      </span>
    </button>
  );
}

export default function VehicleList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canBulkExcel = ['System Admin', 'Admin'].includes(user?.role);
  const canAddVehicle = canBulkExcel;
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [locationModal, setLocationModal] = useState(null); // { title, labels } | null
  const [bulkOpen, setBulkOpen] = useState(false);
  const dropdownRef = useRef(null);
  const perPage = 10;

  const fetchVehicles = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (statusFilter) filters.status = statusFilter;
      if (typeFilter) filters.vehicle_type = typeFilter;

      const [vehiclesRes, ownersRes] = await Promise.all([
        vehicleService.getAllVehicles(currentPage, perPage, filters),
        vehicleOwnerService.getAll(1, 500).catch(() => ({ data: [] })),
      ]);

      const ownersData = ownersRes?.data || ownersRes || [];
      const ownersList = Array.isArray(ownersData) ? ownersData : [];
      const rawVehicles = vehiclesRes.data.data || [];
      setVehicles((prev) => mergeLoadedPage(currentPage, enrichVehiclesWithOwnerDetails(rawVehicles, ownersList), prev));
      setTotalPages(vehiclesRes.data.pagination.pages);
    } catch (err) {
      Alert.error('Failed to load vehicles');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, typeFilter, currentPage]);

  usePaginatedListFetch({
    fetchPage: fetchVehicles,
    setCurrentPage,
    currentPage,
    resetPageDeps: [search, statusFilter, typeFilter],
    debounceWhen: search,
    debounceMs: 0,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeDropdown]);

  const handleDelete = async (vehicleId, vehicleName) => {
    setActiveDropdown(null);
    
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete "${vehicleName}"? This action cannot be undone.`,
      'Delete Vehicle'
    );
    
    if (!confirmed) return;
    
    try {
      const response = await vehicleService.deleteVehicle(vehicleId);
      Alert.success(response.data?.message || 'Vehicle deleted successfully');
      
      if (vehicles.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchVehicles();
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete vehicle');
    }
  };

  const handleViewVehicle = (vehicleId) => {
    console.log("[v0] handleViewVehicle called with ID:", vehicleId);
    setActiveDropdown(null);
    console.log("[v0] About to navigate to:", `/vehicles/view/${vehicleId}`);
    navigate(`/vehicles/view/${vehicleId}`);
  };

  const toggleDropdown = (vehicleId) => {
    setActiveDropdown(activeDropdown === vehicleId ? null : vehicleId);
  };

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setTypeFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = search || statusFilter || typeFilter;

  const openLocationModal = (title, points) => {
    const { labels } = formatLocationPointsDisplay(points);
    if (labels.length === 0) return;
    setLocationModal({ title, labels });
  };

  const LocationPointsCell = ({ points, label }) => {
    const { first, extraCount, labels } = formatLocationPointsDisplay(points);
    if (labels.length === 0) {
      return <span className="text-gray-400">—</span>;
    }
    return (
      <button
        type="button"
        onClick={() => openLocationModal(label, points)}
        title={labels.join(', ')}
        className="text-left text-gray-700 hover:text-buttonhoverTxt hover:underline truncate max-w-full"
      >
        {first}
        {extraCount > 0 && (
          <span className="ml-1 text-xs font-semibold text-buttonhoverTxt">+{extraCount}</span>
        )}
      </button>
    );
  };

  return (
    <DashboardLayout>
      <div className="w-full min-w-0 max-w-full mx-auto">
        {/* Header */}
        <ListPageHeader
          title="Vehicles"
          icon={Truck}
          className="mb-4 sm:mb-4"
        >
          {canBulkExcel ? (
            <>
              <Button
                mobileIconOnly
                variant="secondary"
                icon={FileDown}
                onClick={async () => {
                  try {
                    const res = await vehicleService.downloadBulkExport();
                    await downloadBlob(
                      res.data,
                      'vehicles_export.xlsx',
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    );
                  } catch (err) {
                    Alert.error(err.response?.data?.error || 'Failed to export Excel');
                  }
                }}
              >
                Export
              </Button>
              <Button mobileIconOnly variant="secondary" icon={Sheet} onClick={() => setBulkOpen(true)}>
                Excel
              </Button>
            </>
          ) : null}
          {canAddVehicle && (
            <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/vehicles/add')}>
              Add Vehicle
            </Button>
          )}
        </ListPageHeader>

        <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('common.search')}</label>
            <SearchSubmitInput
              value={search}
              onSearch={setSearch}
              placeholder="Name or number..."
              inputClassName="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Vehicle Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
            >
              <option value="">All Types</option>
              {VEHICLE_TYPE_OPTIONS.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
        </ListFilterPanel>

        {loading && currentPage === 1 ? (
          <div className="flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message="Loading vehicles..." />
          </div>
        ) : vehicles.length === 0 ? (
          <div className="p-8 text-center text-gray-600">No vehicles found</div>
        ) : (
          <>
            {/* Table - Desktop View */}
            <div className="hidden xl:block bg-white rounded-xl shadow-sm w-full min-w-0">
              <div className="w-full overflow-x-auto overscroll-x-contain">
                <table className="w-full min-w-[1200px] border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-gray-700 border-b">
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">VEHICLE NUMBER</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">TYPE</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">CAPACITY (MT)</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">CONTRACT AMT</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap min-w-[120px]">PICKUP</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap min-w-[120px]">DROP</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap min-w-[120px]">VEHICLE OWNER</th>
                      <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">STATUS</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">CREATED AT</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">ACTIONS</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200">
                    {vehicles.map((vehicle) => (
                      <tr key={vehicle.id} className="hover:bg-gray-50 transition-colors relative">
                        {/* VEHICLE NUMBER */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <VehicleNumberPlate
                            number={vehicle.vehicle_number}
                            onClick={() => handleViewVehicle(vehicle.id)}
                          />
                        </td>

                        {/* TYPE */}
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                          <span className="px-2 py-1 text-gray-700 text-sm">
                            {formatVehicleType(vehicle.vehicle_type)}
                          </span>
                        </td>

                        {/* CAPACITY */}
                        <td className="px-4 py-3 text-gray-700 text-base whitespace-nowrap">
                          {vehicle.capacity != null ? `${vehicle.capacity} MT` : '—'}
                        </td>

                        {/* CONTRACT AMOUNT */}
                        <td className="px-4 py-3 text-gray-700 text-base whitespace-nowrap">
                          {vehicle.vehicle_contract_amount
                            ? `₹${Number(vehicle.vehicle_contract_amount).toLocaleString('en-IN')}`
                            : '—'}
                        </td>

                        {/* PICKUP */}
                        <td className="px-4 py-3 text-gray-700 text-sm min-w-[120px]">
                          <LocationPointsCell points={vehicle.pickup_point} label={`Pickup Points — ${vehicle.vehicle_number}`} />
                        </td>

                        {/* DROP */}
                        <td className="px-4 py-3 text-gray-700 text-sm min-w-[120px]">
                          <LocationPointsCell points={vehicle.drop_point} label={`Drop Points — ${vehicle.vehicle_number}`} />
                        </td>

                        {/* VEHICLE OWNER */}
                        <td className="px-4 py-3 min-w-[120px]">
                          <VehicleOwnerTooltip vehicle={vehicle} />
                        </td>

                        {/* STATUS */}
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full font-semibold text-xs ${
                            vehicle.status === "active" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                          }`}>
                            {capitalizeStatus(vehicle.status)}
                          </span>
                        </td>

                        {/* CREATED AT */}
                        <td className="px-4 py-3 text-gray-700 text-base whitespace-nowrap">
                          {new Date(vehicle.created_at).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </td>

                        {/* ACTIONS */}
                        <td className="px-4 py-3 relative whitespace-nowrap">
                          <RowActionsMenu
                            isOpen={activeDropdown === vehicle.id}
                            onToggle={() => toggleDropdown(vehicle.id)}
                            onClose={() => setActiveDropdown(null)}
                            menuRef={dropdownRef}
                            onView={() => handleViewVehicle(vehicle.id)}
                            onEdit={() => navigate(`/vehicles/edit/${vehicle.id}`)}
                            editLabel={t("common.edit")}
                            onDelete={() => handleDelete(vehicle.id, vehicle.vehicle_name)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </div>

            {/* Mobile Card View */}
            <div className="xl:hidden space-y-4">
              {vehicles.map((vehicle) => (
                <div key={vehicle.id} className="bg-white rounded-xl shadow p-4">
                  <div className="flex justify-between items-start mb-3 gap-2">
                    <div className="flex-1 min-w-0">
                      <VehicleNumberPlate
                        number={vehicle.vehicle_number}
                        onClick={() => handleViewVehicle(vehicle.id)}
                      />
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                        vehicle.status.toLowerCase() === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {vehicle.status.toUpperCase()}
                      </span>
                      <RowActionsMenu
                        isOpen={activeDropdown === vehicle.id}
                        onToggle={() => toggleDropdown(vehicle.id)}
                        onClose={() => setActiveDropdown(null)}
                        menuRef={dropdownRef}
                        onView={() => handleViewVehicle(vehicle.id)}
                        onEdit={() => navigate(`/vehicles/edit/${vehicle.id}`)}
                        onDelete={() => handleDelete(vehicle.id, vehicle.vehicle_name)}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                    <div>
                      <span className="text-gray-500 font-medium">Type:</span>
                      <p className="mt-0.5">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                          {formatVehicleType(vehicle.vehicle_type)}
                        </span>
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500 font-medium">Capacity:</span>
                      <p className="text-gray-900 mt-0.5">
                        {vehicle.capacity != null ? `${vehicle.capacity} MT` : '—'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500 font-medium">Contract:</span>
                      <p className="text-gray-900 mt-0.5">
                        {vehicle.vehicle_contract_amount
                          ? `₹${Number(vehicle.vehicle_contract_amount).toLocaleString('en-IN')}`
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500 font-medium">Pickup:</span>
                      <div className="mt-0.5">
                        <LocationPointsCell points={vehicle.pickup_point} label={`Pickup Points — ${vehicle.vehicle_number}`} />
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500 font-medium">Drop:</span>
                      <div className="mt-0.5">
                        <LocationPointsCell points={vehicle.drop_point} label={`Drop Points — ${vehicle.vehicle_number}`} />
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500 font-medium">Owner:</span>
                      <div className="mt-0.5">
                        <VehicleOwnerTooltip vehicle={vehicle} className="min-w-0" />
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500 font-medium">Created:</span>
                      <p className="text-gray-900 mt-0.5">{new Date(vehicle.created_at).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}</p>
                    </div>
                  </div>
                </div>
              ))}
              
            </div>
            <ListPagination
              currentPage={currentPage}
              totalPages={totalPages}
              loading={loading && currentPage > 1}
              onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
            />
          </>
        )}
      </div>

      {/* Location Points Modal */}
      {locationModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4"
          onClick={() => setLocationModal(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="font-semibold text-gray-800 text-base">{locationModal.title}</h3>
              <button
                type="button"
                onClick={() => setLocationModal(null)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-4 space-y-2">
              {locationModal.labels.map((loc, idx) => (
                <div
                  key={`${loc}-${idx}`}
                  className="px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-700"
                >
                  {loc}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <BulkExcelModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk Vehicles"
        description="Download the empty Excel, or use Export for the vehicles already saved. Upload either file. Existing vehicle numbers are skipped. Extra rows you add are created."
        onDownloadTemplate={async () => {
          const res = await vehicleService.downloadBulkTemplate();
          await downloadBlob(
            res.data,
            'vehicles_bulk.xlsx',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          );
        }}
        onUploadFile={(file) => vehicleService.uploadBulkExcel(file)}
        onImported={() => fetchVehicles()}
      />
    </DashboardLayout>
  );
}